<script>
  import { createData } from "$lib/state/data.svelte.js";
  import { createMapData } from "$lib/state/map.svelte.js";
  import { Block, LoadingWrapper, Analytics } from "@urbaninstitute/dataviz-components";
  import VizWrapper from "$components/VizWrapper/VizWrapper.svelte";
  import Linechart from "$components/Linechart/Linechart.svelte";
  import Map from "$components/Map/Map.svelte";
  import MetricSelection from "$components/MetricSelection/MetricSelection.svelte";
  import SideBarControls from "$components/Controls/SideBarControls.svelte";
  import StickyControls from "$components/Controls/StickyControls.svelte";
  import { getTotalHeaderHeight } from "$utils/pageHeader.js";
  import { formatTextTemplate, cleanPercents } from "$utils/dynamicText.js";
  import { sentenceCase } from "$utils/formats.js";
  import { onMount } from "svelte";
  import { GEO_LEVELS } from "$utils/consts.js";

  const data = createData();
  const mapData = createMapData();

  const DOWNLOAD_FILENAME = "UrbanInstitute-affordability-tracker-chart-download";
  const STICKY_HEADER_MIN_HEIGHT = 80;
  const LAYOUT_BREAKPOINT = 1024;

  let menuTriggerEl = $state();
  let windowWidth = $state(0);
  let menuActive = $state(false);
  let pageHeaderHeight = $state(0);
  let stickyHeaderHeight = $state(STICKY_HEADER_MIN_HEIGHT);
  let computedStickyHeaderHeight = $derived(Math.max(stickyHeaderHeight, STICKY_HEADER_MIN_HEIGHT));
  let metricSelectionData = $derived(
    data.seriesOverviews.map(({ series, label }) => ({ value: series, label }))
  );
  let headerInterval;
  let sideBarActive = $state(false);
  let focusGeo = $derived(
    data.currentLevel === GEO_LEVELS.DISTRICT && data.currentDistrict
      ? data.currentDistrict
      : data.currentState
  );
  let descriptionData = $derived(
    data.getDescriptionData(data.currentLevel, focusGeo, data.currentMetric)
  );
  let mapDescriptionData = $derived(
    data.getMapDescriptionData(data.currentLevel, focusGeo, data.currentMetric)
  );
  let descriptionTemplate = $derived(
    descriptionData.firstValue
      ? `description_${descriptionData.descriptionTemplate}`
      : `description_fallback_${descriptionData.descriptionTemplate}`
  );
  let mapDescriptionTemplate = $derived(
    mapDescriptionData.nationalValue
      ? `description_map_${mapDescriptionData.descriptionTemplate}`
      : `description_map_fallback_${mapDescriptionData.descriptionTemplate}`
  );
  let headerTemplate = $derived(`header_${descriptionData.headerTemplate}`);
  let descriptionText = $derived(
    sentenceCase(
      cleanPercents(formatTextTemplate(data.labels[descriptionTemplate], descriptionData))
    )
  );
  let mapDescriptionText = $derived(
    sentenceCase(
      cleanPercents(formatTextTemplate(data.labels[mapDescriptionTemplate], mapDescriptionData))
    )
  );
  let mobileMenu = $derived(windowWidth < LAYOUT_BREAKPOINT);

  function onObserve(entries) {
    for (const entry of entries) {
      // toggle menu active state based on whether the trigger element is intersecting and above the viewport
      menuActive = entry.boundingClientRect.top < pageHeaderHeight;
    }
  }
  let observerOptions = $derived({
    // Trigger when element is completely out of view
    rootMargin: `-${pageHeaderHeight}px 0px 0px 0px`,
    threshold: 0
  });

  function watchHeader() {
    headerInterval = setInterval(() => {
      pageHeaderHeight = getTotalHeaderHeight();
    }, 200);
  }

  function unWatchHeader() {
    clearInterval(headerInterval);
  }

  onMount(() => {
    watchHeader();
    return () => {
      unWatchHeader();
    };
  });

  $effect(() => {
    if (data.dataReady && menuTriggerEl) {
      // create observer and begin observation
      const observer = new IntersectionObserver(onObserve, observerOptions);
      observer.observe(menuTriggerEl);
      return () => {
        observer.unobserve(menuTriggerEl);
      };
    }
  });
</script>

<Analytics
  pageTitle="The American Affordability Tracker"
  mode={import.meta.env.MODE}
  sendPageview={false}
/>
<svelte:window bind:innerWidth={windowWidth} />
{#if data.dataReady}
  <div
    class="tool-container"
    bind:this={data.toolContainerEl}
    style:--page-header-height="{pageHeaderHeight}px"
    style:--sticky-header-height="{computedStickyHeaderHeight}px"
  >
    <div
      class="sticky-control-wrapper"
      class:active={menuActive}
      bind:clientHeight={stickyHeaderHeight}
    >
      <Block width="wide">
        <StickyControls
          bind:value={data.currentMetric}
          {metricSelectionData}
          controlsOpen={sideBarActive}
          toggleControls={() => (sideBarActive = !sideBarActive)}
        />
      </Block>
    </div>
    {#if mobileMenu}
      <div class="sidebar" class:active={sideBarActive}>
        <div class="sidebar-inner">
          <SideBarControls toggleControls={() => (sideBarActive = false)} />
        </div>
      </div>
    {/if}
    <div class="tool-top-spacing">
      <Block>
        <h4 class="metric-selection-title">{data.labels.tool_header}</h4>
        <p class="metric-selection-description">{data.labels.tool_description}</p>
      </Block>
      <div class="tool-sticky-menu-indicator" bind:this={menuTriggerEl}></div>
    </div>
    <Block width="wide">
      <div class="metric-selection-container">
        <MetricSelection bind:value={data.currentMetric} metrics={data.seriesOverviews}
        ></MetricSelection>
      </div>
    </Block>
    <Block width="wide">
      <div class="primary-tool-container" id="data-tool-primary-tool-container">
        {#if !mobileMenu}
          <div class="sidebar" class:active={sideBarActive}>
            <div class="sidebar-inner">
              <SideBarControls toggleControls={() => (sideBarActive = false)} />
            </div>
          </div>
        {/if}
        <div class="tool-content">
          <VizWrapper
            downloadFilename={DOWNLOAD_FILENAME}
            downloadLabel={data.labels.download_chart_label}
          >
            {#snippet title()}
              <h4 class="chart-title">
                {formatTextTemplate(data.labels[`trend_${headerTemplate}`], {
                  metric: data.currentMetadata?.short_label || ""
                })}
              </h4>
            {/snippet}
            {#snippet description()}
              <p class="chart-description">{descriptionText}</p>
            {/snippet}
            {#if data.currentLevel === "state"}
              <Linechart data={data.currentChartData} highlightGeo={data.currentState} />
            {:else}
              <Linechart data={data.currentChartData} highlightGeo={data.currentDistrict} />
            {/if}
            {#snippet source()}
              <p class="source-line">
                <strong>Source:</strong>
                {@html data.currentMetadata.source}
              </p>
            {/snippet}
            {#snippet notes()}
              <p class="source-line"><strong>Notes:</strong> {@html data.currentMetadata.notes}</p>
            {/snippet}
          </VizWrapper>
          <VizWrapper
            downloadFilename={DOWNLOAD_FILENAME}
            downloadLabel={data.labels.download_map_label}
          >
            {#snippet title()}
              <h4 class="chart-title">
                {formatTextTemplate(data.labels[`map_${headerTemplate}`], {
                  metric: data.currentMetadata?.short_label || ""
                })}
              </h4>
            {/snippet}
            {#snippet description()}
              <p class="chart-description">{mapDescriptionText}</p>
            {/snippet}
            {#snippet children(isDownload)}
              <Map data={data.currentMapData} zoomable={!isDownload} />
            {/snippet}
            {#snippet source()}
              <p class="source-line">
                <strong>Source:</strong>
                {@html data.currentMetadata.source}
              </p>
            {/snippet}
            {#snippet notes()}
              <p class="source-line"><strong>Notes:</strong> {@html data.currentMetadata.notes}</p>
            {/snippet}
          </VizWrapper>
        </div>
      </div></Block
    >
    <Block>
      <div class="tool-source-info">
        <p class="source-line"><strong>Source:</strong> {@html data?.currentMetadata?.source}</p>
        <p class="source-line"><strong>Notes:</strong> {@html data?.currentMetadata?.notes}</p>
      </div>
    </Block>
  </div>
{:else}
  <div class="loading-wrapper">
    <LoadingWrapper />
    <h4>Loading</h4>
  </div>
{/if}

<style>
  .tool-top-spacing {
    margin-top: calc(var(--sticky-header-height) * -1);
  }
  .metric-selection-title {
    margin-bottom: var(--spacing-2) !important;
  }
  .metric-selection-description {
    font-size: var(--font-size-large) !important;
    line-height: 28px !important;
    font-style: italic;
  }
  .loading-wrapper {
    height: 250px;
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
    flex-direction: column;
    gap: var(--spacing-4);
  }
  .tool-container {
    width: 100%;
    margin: 0 auto;
    padding-bottom: var(--spacing-16);
    --all-header-height: calc(var(--page-header-height) + var(--sticky-header-height));
  }
  .metric-selection-container {
    margin-inline: auto;
    margin-block-end: var(--spacing-16);
  }
  .primary-tool-container {
    margin-top: 32px;
    scroll-margin-top: var(--all-header-height, 0);
    position: relative;
  }
  .tool-content {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-16);
  }
  .sidebar {
    --transition-duration: 200ms;
    width: 100%;
    position: sticky;
    top: var(--all-header-height, 0);
    left: var(--spacing-4);
    background: var(--color-white);
    z-index: 500;
    opacity: 0;
    pointer-events: none;
    transform: translateY(-100%);
    transition: transform var(--transition-duration) ease;
    z-index: 300;
    padding-bottom: var(--spacing-4);
    padding-top: var(--spacing-4);
    padding-inline: var(--spacing-4);
    height: 0;
  }
  .sidebar.active {
    pointer-events: auto;
    display: block;
    opacity: 1;
    transform: translateY(0);
    height: auto;
    border-bottom: solid 1px var(--color-gray-shade-light);
    margin-bottom: var(--spacing-8);
  }
  .chart-description,
  .chart-title {
    margin-bottom: var(--spacing-4);
    margin-top: 0;
  }
  .chart-title {
    font-size: 28px !important;
  }
  .tool-sticky-menu-indicator {
    transform: translateY(-000px);
  }
  .sticky-control-wrapper {
    background: var(--color-white);
    border-bottom: solid 1px var(--color-gray-shade-light);
    position: sticky;
    top: var(--page-header-height, 0);
    z-index: 500;
    transform: translateY(-100%);
    transition: transform 400ms ease;
    padding-block: var(--spacing-6);
    opacity: 0;
    pointer-events: none;
    /* height: 0; */
  }
  @media (prefers-reduced-motion: reduce) {
    .sticky-control-wrapper {
      transition: none;
    }
  }
  .sticky-control-wrapper.active {
    transform: translateY(0);
    opacity: 1;
    height: auto;
    pointer-events: auto;
  }
  .source-line {
    font-size: var(--font-size-small) !important;
    line-height: 21px !important;
  }
  .source-line:last-child {
    margin-bottom: 0 !important;
  }
  @media (min-width: 1024px) {
    .tool-content {
      width: 80%;
    }
    .primary-tool-container {
      display: flex;
      gap: var(--spacing-4);
    }
    .sidebar {
      display: block;
      width: 20%;
      position: sticky;
      left: auto;
      z-index: 100;
      padding-bottom: 0;
      padding-top: 0;
      padding-inline: 0;
      border-bottom: none;
      height: auto;
      opacity: 1;
      pointer-events: auto;
      transform: translateY(0);
    }
    .sidebar-inner {
      position: sticky;
      padding-top: var(--spacing-8);
      top: var(--all-header-height, 0);
    }
  }
</style>
